import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  OnChanges,
  ElementRef,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceService } from '@core/services/device.service';
import { ReqService } from '@core/services/req.service';
import { URLService } from '@core/services/url.service';
import { IHotelImage } from '@core/models/hotel';
import { MbCarousel } from '@am/moebius';
import { VerticalSliderComponent } from '../vertical-slider/vertical-slider.component';
import { BackgroundImageDirective } from '@shared/directives/background-image.directive';
import { NgClass } from '@angular/common';

@Component({
    selector: 'slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    standalone: true,
    imports: [
      NgClass,
      MbCarousel,
      BackgroundImageDirective,
      VerticalSliderComponent,
    ],
})
export class SliderComponent implements OnInit, AfterViewInit, OnChanges {
  public static MAX_SLIDER_ITEM = 52;
  @Input()
  items: IHotelImage[];
  @Input()
  itemsCant: number;
  @Output()
  clickShowGallery: EventEmitter<number> = new EventEmitter<number>();
  @Input()
  showMiniGallery: boolean;
  @Input()
  showLabel: boolean;
  @Input()
  position: number;
  @Input()
  showVertical = false;

  @ViewChild('swiperContainerGallery')
  swiperContainerGallery: ElementRef;
  @ViewChild(MbCarousel)
  mbCarousel: MbCarousel;
  @ViewChild(MbCarousel, { read: ChangeDetectorRef })
  mbCarouselCdr: ChangeDetectorRef;
  @ViewChild(VerticalSliderComponent)
  carouselVertical: VerticalSliderComponent;

  public currentPage: number;
  public pages: number[];
  public swiperGallery: any;
  public swiperImage: any;
  public showButtonsNavigation: boolean;
  public heroImage: IHotelImage;
  public brand: string;
  public srcImages: string[] = [];
  public isMobile = true;
  constructor(
    private analyticsService: AnalyticsService,
    private deviceService: DeviceService,
    private reqService: ReqService,
    private cd: ChangeDetectorRef,
    private urlService: URLService,
  ) {}

  ngOnInit() {
    this.brand = this.reqService.getBrand();
    this.currentPage = this.position ? this.position : 0;
    this.initItemsAndPages();
    this.isMobile = this.deviceService.isMobile();
    this.showButtonsNavigation = !(this.showMiniGallery && this.deviceService.isMobile());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items && changes.items.currentValue) {

      this.items = changes.items.currentValue.map((item: IHotelImage) => ({
        ...item,
        url: this.urlService.getResizeUrl(item.url, 'detail'),
      }));

      this.initItemsAndPages();
      this.srcImages = this.items.map((item: IHotelImage) => item.url);

      if (this.showMiniGallery && this.swiperGallery) {
        this.updateSliders();
        this.slideToSliders(0);
      }
    }
  }

  ngAfterViewInit() {
    if (this.showMiniGallery) {
      this.initSwiperGallery();
    }

    this.mbCarousel.next.subscribe((response: number) => {
      if (response < this.currentPage) {
        this.mbCarousel.nextSlide();
      } else {
        this.currentPage = response;
        this.slideToSliders(this.currentPage);
      }
      this.verticalLeft();
    });

    this.mbCarousel.prev.subscribe((response: number) => {
      this.currentPage = response;
      this.slideToSliders(this.currentPage);
      this.verticalRight();
    });

    if (this.currentPage > 0) {
      this.mbCarousel.nextSlide();
    }

    this.cd.detectChanges();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.showMiniGallery || !this.mbCarousel) return;
    if (event.key === 'ArrowRight') {
      this.mbCarousel.nextSlide();
    } else if (event.key === 'ArrowLeft') {
      this.mbCarousel.prevSlide();
    }
  }

  /**
   * Event when user click the previous icon, previous image
   * @return void
   */
  public slidePrevious() {
    this.swiperImage.slidePrev();
    this.verticalRight();
  }

  /**
   * Event when user click the next icon, next image
   * @return void
   */
  public slideNext() {
    this.swiperImage.slideNext();
    this.verticalLeft();
  }

  public verticalLeft() {
    if (this.showVertical) {
      this.carouselVertical.nextSlider();
    }
  }

  public verticalRight() {
    if (this.showVertical) {
      this.carouselVertical.backSlider();
    }
  }

  /**
   * Create the first swiper for "Images" with loop and configuration when user move via keyboard
   * Add event click, emit event to open gallery
   * @return void
   */

  /**
   * Track event to analytics, emit event to open gallery
   * @param {number} index
   */
  public onCarouselClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('.mb-carousel__btn-prev, .mb-carousel__btn-next')) return;
    if (this.showLabel && this.items?.length > 0) {
      this.showGallery(this.currentPage);
    }
  }

  public showGallery(index: number) {
    this.analyticsService.trackEvent({
      section: 'Detail',
      action: 'Modal Gallery',
      label: 'Show',
    });
    this.clickShowGallery.emit(index);
  }

  /**
   * Create the second swiper for "Mini Gallery" add configuration breakpoints for mobile, desktop, tablet
   * loop, loop slides
   * Move both swiper to the current page when user click (default)
   * @return void
   */
  private initSwiperGallery() {
    this.swiperGallery = new Swiper(this.swiperContainerGallery.nativeElement, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      centeredSlides: true,
    });
    this.slideToSliders(this.currentPage);
  }

  /**
   * Initialize items and truncate items if exceeds max slider items for view
   * @return void
   */
  private initItemsAndPages() {
    if (this.items && this.items.length > SliderComponent.MAX_SLIDER_ITEM) {
      this.items = this.items.slice(0, SliderComponent.MAX_SLIDER_ITEM);
    }
    this.pages = new Array(this.items.length / this.itemsCant);
  }

  /**
   * Update all sliders and move to first image
   * @return void
   */
  private updateSliders() {
    setTimeout(() => {
      this.swiperGallery.update();
    }, 10);
  }

  /**
   * Slide all sliders to position recive
   * @param {number} position
   * @return void
   */
  private slideToSliders(position: number) {
    if (this.swiperGallery) {
      this.swiperGallery.slideTo(position);
    }
  }

  public onVerticalItemClick(index: number) {
    if (this.showLabel && this.items?.length > 0) {
      this.showGallery(index);
    }
  }

  public onArrowClick(targetIndex: number) {
    if (targetIndex === this.currentPage) return;

    const carousel = this.mbCarousel as any;
    carousel.isTransforming = false;

    if (targetIndex > this.currentPage) {
      carousel.currentIndex.set(targetIndex - 1);
      this.mbCarousel.nextSlide();
    } else {
      carousel.currentIndex.set(targetIndex + 1);
      this.mbCarousel.prevSlide();
      this.mbCarouselCdr?.detectChanges();
    }

    this.currentPage = targetIndex;
    this.slideToSliders(targetIndex);
  }

  private getImagesUrl() {
    const images = [];
    this.items.forEach((item: IHotelImage) => {
      if (item) {
        images.push(item.url);
      }
    });
    return images;
  }

  addSizeUrl() {
    this.items = this.items.map((item: IHotelImage) => {
      if (item) {
        item.url = this.urlService.getResizeUrl(item.url ,'detail');
      }
      return item;
    });
  }
}
